import type { MessageKeys, TypedFormatMessageFn } from 'types/intl';
import { ITEM_TITLE_KEYS } from '../config';
import { CASE_SIDEBAR_SECTIONS } from '../config/sections';
import { getVisibleOoCaseSidebarItemIds, getVisibleOoItemIdsForSection } from '../buildOoCaseSidebar';
import type { OoCaseSidebarContext } from '../buildOoCaseSidebar';
import { CaseSidebarItemId, CaseSidebarSection } from '../types';
import { mapOoCaseSidebarItemToUrlSection, resolveOoCaseSidebarItemId } from './ooFormSectionMapping';

function getOoCaseSidebarSectionForItemId(
  itemId: CaseSidebarItemId,
  context: OoCaseSidebarContext,
): CaseSidebarSection | undefined {
  for (const section of CASE_SIDEBAR_SECTIONS) {
    const items = getVisibleOoItemIdsForSection(section, context);

    if (items.includes(itemId)) {
      return section;
    }
  }

  return undefined;
}

export function isLastVisibleItemInOoCaseSidebarSection(
  itemId: CaseSidebarItemId,
  context: OoCaseSidebarContext,
): boolean {
  const section = getOoCaseSidebarSectionForItemId(itemId, context);

  if (!section) {
    return false;
  }

  const sectionItems = getVisibleOoItemIdsForSection(section, context);

  return sectionItems[sectionItems.length - 1] === itemId;
}

export function getOoCaseSidebarNavigationItemIds(context: OoCaseSidebarContext): CaseSidebarItemId[] {
  return getVisibleOoCaseSidebarItemIds(context);
}

export function getAdjacentOoCaseSidebarItemId(
  currentSection: string,
  context: OoCaseSidebarContext,
  direction: 'next' | 'previous',
): CaseSidebarItemId | undefined {
  const currentItemId = resolveOoCaseSidebarItemId(currentSection, context);

  if (!currentItemId) {
    return undefined;
  }

  const navigationItemIds = getOoCaseSidebarNavigationItemIds(context);
  const currentIndex = navigationItemIds.indexOf(currentItemId);

  if (currentIndex === -1) {
    return undefined;
  }

  if (direction === 'next' && isLastVisibleItemInOoCaseSidebarSection(currentItemId, context)) {
    return undefined;
  }

  const adjacentIndex = direction === 'next' ? currentIndex + 1 : currentIndex - 1;

  return navigationItemIds[adjacentIndex];
}

export function getAdjacentOoCaseSidebarUrlSection(
  currentSection: string,
  context: OoCaseSidebarContext,
  direction: 'next' | 'previous',
): string | undefined {
  const adjacentItemId = getAdjacentOoCaseSidebarItemId(currentSection, context, direction);

  if (!adjacentItemId) {
    return undefined;
  }

  return mapOoCaseSidebarItemToUrlSection(adjacentItemId);
}

export function getOoCaseSidebarNavigationLabel(
  formatMessage: TypedFormatMessageFn,
  currentSection: string,
  context: OoCaseSidebarContext,
  direction: 'next' | 'previous',
): string | undefined {
  const adjacentItemId = getAdjacentOoCaseSidebarItemId(currentSection, context, direction);

  if (!adjacentItemId) {
    return undefined;
  }

  const title = formatMessage({ id: ITEM_TITLE_KEYS[adjacentItemId] as MessageKeys });
  const prefix = direction === 'next' ? 'Next' : 'Back';

  return `${prefix}: ${title}`;
}
